// AI-CONTEXT: See docs/features/update-aanvragen.md
// Hoe erg een updateverzoek voor de indiener is, met de Nederlandse labels en
// de sorteervolgorde voor de triage bij Deccos.
//
// Dit bestand is de BRON. `functions/updateRequests/updateRequestConstants.js`
// spiegelt deze waarden — src/ en functions/ zijn losse npm-pakketten zonder
// gedeelde modulegrens, dus niets verbindt de twee bij de build en een
// afwijking is stil. De pariteitstest aan de functions-kant faalt zodra deze
// lijst verandert. Repareer dan de spiegel, nooit dit bestand.
// AI-CONTEXT: See docs/patterns.md#data-mirrored-constants

/**
 * @typedef {'blokkerend' | 'verstorend' | 'vervelend' | 'wens'} UpdateRequestSeverity
 */

/**
 * Van het ergste naar het lichtste — de volgorde IS de triagevolgorde, en de
 * UI toont ze zo.
 *
 * Vier niveaus, geen vijf: het onderscheid moet voor een invuller zonder
 * software-achtergrond in één zin te maken zijn. De gangbare ticketschalen
 * (blocker/critical/major/minor/trivial) splitsen bovenaan verder uit, maar
 * daar zit voor ons geen verschil in werk — alles wat blokkeert gaat voor.
 */
const UPDATE_REQUEST_SEVERITIES = /** @type {Array<UpdateRequestSeverity>} */ ([
    'blokkerend',
    'verstorend',
    'vervelend',
    'wens',
])

/** @type {Record<UpdateRequestSeverity, string>} */
const UPDATE_REQUEST_SEVERITY_LABELS = {
    'blokkerend': 'Blokkerend',
    'verstorend': 'Verstorend',
    'vervelend': 'Vervelend',
    'wens': 'Wens',
}

/**
 * De zin die naast het label staat. Zonder uitleg kiest iedereen anders: 'ernstig'
 * betekent voor de een 'ik kan niet werken' en voor de ander 'ik vind het erg'.
 *
 * @type {Record<UpdateRequestSeverity, string>}
 */
const UPDATE_REQUEST_SEVERITY_HINTS = {
    'blokkerend': 'Ik kan mijn werk hierdoor niet doen',
    'verstorend': 'Het lukt wel, maar alleen met een omweg',
    'vervelend': 'Het werkt, maar het kost onnodig moeite',
    'wens': 'Er is niets kapot; dit zou het beter maken',
}

/** Wat er staat bij de verzoeken van vóór dit veld, en bij een kapotte waarde. */
const UNKNOWN_SEVERITY_LABEL = 'Niet opgegeven'

/**
 * @param {*} severity - Waarde uit een UpdateRequests-document
 * @returns {boolean} True als het een van de vier afgesproken waarden is
 */
const isUpdateRequestSeverity = (severity) => UPDATE_REQUEST_SEVERITIES.includes(severity)

/**
 * De positie in de triagevolgorde, voor `sortRank` van de gedeelde tabel.
 *
 * Een onbekende of ontbrekende ernst geeft -1. sortItems leest een negatieve
 * rang als 'leeg' en zet die rij onderaan, in béide richtingen — anders zou
 * 'Niet opgegeven' als gewone tekst vóór de echte labels sorteren en stonden
 * de 145 oude verzoeken bovenaan de triagelijst.
 * AI-CONTEXT: See docs/pitfalls.md#ui-display-fallback-used-as-sort-key
 *
 * @param {*} severity - Waarde uit een UpdateRequests-document
 * @returns {number} 0 voor het ergste, hoger voor lichter, -1 als er niets staat
 */
const updateRequestSeverityRank = (severity) => UPDATE_REQUEST_SEVERITIES.indexOf(severity)

/**
 * Het label dat de gebruiker leest.
 *
 * LET OP: weergavewaarde, geen sorteersleutel — gebruik `updateRequestSeverityRank`.
 *
 * @param {*} severity - Waarde uit een UpdateRequests-document
 * @returns {string} Nederlands label; nooit leeg
 */
const updateRequestSeverityLabel = (severity) => (
    isUpdateRequestSeverity(severity) ? UPDATE_REQUEST_SEVERITY_LABELS[severity] : UNKNOWN_SEVERITY_LABEL
)

export {
    UPDATE_REQUEST_SEVERITIES,
    UPDATE_REQUEST_SEVERITY_LABELS,
    UPDATE_REQUEST_SEVERITY_HINTS,
    UNKNOWN_SEVERITY_LABEL,
    isUpdateRequestSeverity,
    updateRequestSeverityRank,
}

export default updateRequestSeverityLabel
